import { useAlert } from 'react-alert';
import { useHistory } from 'react-router-dom';
import I18n from 'i18n-js';

import { vote } from '@lootboy/lib/slices/surveys';
import { TDetailedEnabledSurvey } from '@lootboy/lib/slices/surveys/types';
import { SECOND_MS } from '@lootboy/lib/time';

import Alert from '~/components/Alert';
import ButtonClean from '~/components/ButtonClean';
import Image from '~/components/Image';
import NewTabOpener from '~/components/NewTabOpener';

import { useRedirectToLogin } from '~/hooks/useRedirectToLogin';
import { useTimeout } from '~/hooks/useTimeout';
import { useLoggedIn } from '~/redux/hooks/useLoggedIn';
import { useAppDispatch } from '~/redux/store/useAppDispatch';

import styles from './VoteItem.module.scss';

type TVoteItemProps = {
  voteItem:
    | TDetailedEnabledSurvey['votingOptions'][number]
    | NonNullable<TDetailedEnabledSurvey['votingResults']>[number];
  maxScale: number;
  surveyId: string;
  disabled: boolean;
};

const VOTING_ANIMATION_DURATION = 600;

function formatPercentage(percentage: number): string {
  return `${Number(percentage.toFixed(2))}%`;
}

function VoteItemChildren({ voteItem }: Pick<TVoteItemProps, 'voteItem'>) {
  return (
    <>
      {voteItem.image && (
        <Image
          alt=""
          path={voteItem.image}
          className={styles.itemAvatar}
          height={60}
          width={60}
        />
      )}
      <span className={styles.itemLabel}>{voteItem.label}</span>
    </>
  );
}
export function VoteItem({
  voteItem,
  maxScale,
  surveyId,
  disabled,
}: TVoteItemProps): JSX.Element {
  const dispatch = useAppDispatch();
  const redirectToLogin = useRedirectToLogin();
  const isLoggedIn = useLoggedIn();
  const history = useHistory();
  const { timeout } = useTimeout();
  const alert = useAlert();

  const hasUserVoted = 'votingPercent' in voteItem;

  const onVoteClick = () => {
    if (!isLoggedIn) {
      redirectToLogin();
      return;
    }

    dispatch(vote({ surveyId, value: voteItem.label }))
      .unwrap()
      .then(({ purchase }) => {
        if (purchase) {
          timeout(VOTING_ANIMATION_DURATION + SECOND_MS, () => {
            history.push(`/purchases/${purchase._id}`);
          });
        }
      })
      .catch(() => {
        alert.error(
          <Alert
            body={I18n.t('Api_SurveysController_error_survey_disabled')}
            title={I18n.t('common_error')}
          />,
        );
      });
  };

  return (
    <li className={styles.item}>
      {voteItem.url ? (
        <NewTabOpener
          href={voteItem.url}
          className={styles.itemLink}
        >
          <VoteItemChildren voteItem={voteItem} />
        </NewTabOpener>
      ) : (
        <VoteItemChildren voteItem={voteItem} />
      )}
      <span className={styles.voteSection}>
        {hasUserVoted ? (
          <span className={styles.result}>
            {formatPercentage(voteItem.votingPercent)}
          </span>
        ) : (
          <ButtonClean
            onClick={onVoteClick}
            className={styles.voteButton}
            disabled={disabled}
          >
            {I18n.t('SurveyView_vote_label')}
          </ButtonClean>
        )}
      </span>
      <div
        className={styles.percentageBar}
        style={{
          maxWidth: hasUserVoted
            ? `${(voteItem.votingPercent * 100) / maxScale}%`
            : 0,
        }}
      />
    </li>
  );
}
